import { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';
import { AxiosError } from 'axios';
import { setClientExternalVendorId, removeClientExternalVendorId } from '../../../../api/clientsApi/clientsApi';
import useSnackBar from '../../../../hooks/useSnackBar';
import { ErrorData } from '../../../../types';
import ConfirmationPopup from '../../../../components/ConfirmationPopup';
import FindNetSuiteVendorPopup from '../../../../components/FindNetSuiteVendorPopup';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Theme,
    Box,
    Tooltip,
    Button,
    useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { numberToUSDCurrency } from '../../../../helpers/numberToUSDCurrency';
import { NETSUITE_LABEL } from '../../../../constants/constants';
import TruncatedText from '../../../../components/TruncatedText';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import LaunchIcon from '@mui/icons-material/Launch';
import SearchIcon from '@mui/icons-material/Search';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { accountingVendorType } from '../../../VendorsView/helpers';

const useStyles = makeStyles<{ isLongData?: boolean }>()(
    (theme: Theme, { isLongData }) => ({
        accordionRoot: {
            backgroundColor: 'transparent',
            marginTop: theme.spacing(1),
            boxShadow: 'none',
            overflow: 'hidden',
            fontFamily: 'Poppins',
        },
        accordionSummary: {
            backgroundColor: theme.palette.primary.light,
            minHeight: '56px',
            padding: '0 16px',
            fontFamily: 'Poppins',
            '& .MuiTypography-root': {
                fontWeight: 600,
                fontSize: '16px',
                color: theme.palette.common.white,
                fontFamily: 'Poppins',
            },
            flexDirection: 'row-reverse'
        },
        accordionDetails: {
            padding: theme.spacing(2),
            backgroundColor: theme.palette.primary.light,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(3),
            fontFamily: 'Poppins',
        },
        tableContainer: {
            width: '99%',
            background: 'transparent',
            border: `1px solid ${theme.palette.secondary.dark}`,
            borderRadius: '8px',
            overflow: 'auto',
            fontSize: '14px',
            lineHeight: '17px',
            fontWeight: 500,
            fontFamily: 'Poppins',
            minHeight: isLongData ? '300px' : 'inherit',
            '&::-webkit-scrollbar': {
                width: '5px',
                height: '5px',
                borderRadius: '20px',
            },
            '&::-webkit-scrollbar-thumb': {
                borderRadius: 20,
                backgroundColor: theme.palette.secondary.dark,
            },
        },
        tableHeaderCell: {
            background: theme.palette.primary.light,
            '& .MuiTableCell-root': {
                textAlign: 'left',
                color: theme.palette.text.secondary,
                borderBottom: '1px solid #515151',
                fontWeight: 500,
                fontFamily: 'Poppins',
                fontSize: '14px',
                height: '35px',
                padding: '16px 12px 16px 8px',
                whiteSpace: 'nowrap',
                minWidth: '120px',
            },
        },
        tableBodyCell: {
            '& .MuiTableCell-root': {
                textAlign: 'left',
                color: theme.palette.common.white,
                borderBottom: '1px solid #4A4E5D',
                fontWeight: 400,
                fontFamily: 'Poppins',
                fontSize: '14px',
                height: '35px',
                padding: '16px 12px 16px 8px',
                minWidth: '120px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            },
        },
        labelText: {
            fontSize: '14px',
            fontWeight: 500,
            fontFamily: 'Poppins',
            color: '#FFFFFFB3',
        },
        labelSmallText: {
            fontSize: '12px',
            fontFamily: 'Poppins',
         },
        netSettlementSummary: {
            width: '96%',
            marginTop: '16px',
            border: `1px solid ${theme.palette.secondary.dark}`,
            borderRadius: '8px',
            backgroundColor: theme.palette.primary.light,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            padding: '16px',
            fontFamily: 'Poppins',
            color: '#FFFFFF',
        },
        summaryRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
        },

        summaryRowIndented: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            marginLeft: theme.spacing(3),   // Indent child rows
        },
        summaryKeyNet: {
            fontSize: '16px',
            fontFamily: 'Poppins',
            color: '#FFFFFFB3',
        },

        summaryKeyGross: {
            fontSize: '14px',
            fontFamily: 'Poppins',
            fontWeight: 500,
            color: '#FFFFFF',
        },

        summaryKeyDeduction: {
            fontSize: '14px',
            fontFamily: 'Poppins',
            fontWeight: 500,
            color: '#FFFFFF',
            marginLeft: '8px'
        },

        summaryKey: {
            fontSize: '14px',
            fontWeight: 500,
            fontFamily: 'Poppins',
            color: '#FFFFFF',
        },

        summaryKeyIndented: {
            fontSize: '14px',
            fontWeight: 500,
            fontFamily: 'Poppins',
            color: '#FFFFFFB3',
            marginLeft: '25px',   // Indent child rows
        },

        summaryKeyBold: {
            fontSize: '14px',
            fontWeight: 600,
            fontFamily: 'Poppins',
            color: '#FFFFFF',
        },

        summaryValue: {
            fontSize: '14px',
            fontWeight: 200,
            fontFamily: 'Poppins',
            color: theme.palette.common.white,
        },
        summaryValueIndented: {
            fontSize: '14px',
            fontWeight: 200,
            fontFamily: 'Poppins',
            color: '#FFFFFFB3',
        },
    }));

interface NetSettlementTableProps {
    handleMenuOptionClick: (option: string, index: number) => void;
    canEdit?: boolean;
    isOnboardingReadOnly?: boolean;
    settledAmount?: string;
    pipAmount?: number;
    lienReimbursementAmount?: number;
    settlementType?: string;
    onSyncStateChange?: (isSynced: boolean) => void;
    sequenceNumber?: number;
    settlementSummary?: {
        attorneysFeesTotal: number;
        netSettlementTotal: number;
        grossSettlement: number;
        total: number;
        liens: number;
        liensWithheld: number;
        advances: number;
        advancesWithheld: number;
        serviceProviders: number;
        serviceProvidersWithheld: number;
    }
    onSync?: () => void;
}

const NetSettlementTable = ({
    handleMenuOptionClick,
    canEdit,
    isOnboardingReadOnly,
    settlementSummary,
    settledAmount,
    pipAmount,
    lienReimbursementAmount,
    settlementType,
    onSyncStateChange,
    sequenceNumber,
    onSync
}: NetSettlementTableProps) => {
    const { watch } = useFormContext();
    const { setAlert } = useSnackBar();
    const theme = useTheme();
    const netSettlementFields = watch('netSettlement');
    const isLongData = netSettlementFields?.length > 3;
    const styles = useStyles({ isLongData });
    const isNetSettlementZero = settlementSummary?.netSettlementTotal === 0;
    const isReadOnly = !canEdit || isNetSettlementZero;
    const isWaveOne = sequenceNumber === 1;

    const vendorType = accountingVendorType;
    // Sync state for plaintiff (net settlement)
    const [isSynced, setIsSynced] = useState<boolean>(false);
    const [netSuiteLink, setNetSuiteLink] = useState<string | null>(null);
    const [hasSyncInit, setHasSyncInit] = useState(false);

    useEffect(() => {
        if (!hasSyncInit && netSettlementFields?.accounting != null) {
            setIsSynced(netSettlementFields.accounting.isSynced ?? false);
            setNetSuiteLink(netSettlementFields.accounting.link ?? null);
            setHasSyncInit(true);
        }
    }, [netSettlementFields, hasSyncInit]);

    const hasAccounting = netSettlementFields?.accounting != null;

    const [syncAction, setSyncAction] = useState<'sync' | 'relink'>('sync');
    const [isNetSuitePopupOpen, setIsNetSuitePopupOpen] = useState(false);
    const [isUnlinkConfirmOpen, setIsUnlinkConfirmOpen] = useState(false);

    const plaintiffName = netSettlementFields?.name || '';
    const effectiveClientId = netSettlementFields?.clientId;

    const handleSyncButtonClick = () => {
        setSyncAction('sync');
        setIsNetSuitePopupOpen(true);
    };

    const handleRelinkButtonClick = () => {
        setSyncAction('relink');
        setIsNetSuitePopupOpen(true);
    };

    // Sync/relink directly from the review popup, no confirmation step
    const handleNetSuiteVendorSelect = async (internalId: string) => {
        if (!effectiveClientId || !internalId) return;
        try {
            const response = await setClientExternalVendorId(effectiveClientId, internalId);
            setAlert(() => ({
                message: `Plaintiff ${syncAction === 'sync' ? 'synced' : 'relinked'} successfully.`,
                type: 'success',
            }));
            const newIsSynced = response.data.isSynced ?? false;
            setIsSynced(newIsSynced);
            setNetSuiteLink(response.data.link ?? null);
            onSyncStateChange?.(newIsSynced);
            setIsNetSuitePopupOpen(false);
            onSync && onSync();

        } catch (error) {
            const axiosError = error as AxiosError<ErrorData>;
            setAlert(() => ({
                message: axiosError.response?.data?.message || `Failed to ${syncAction} plaintiff.`,
                type: 'error',
            }));
        }
    };

    const handleConfirmUnlink = async () => {
        if (!effectiveClientId) return;
        try {
            const response = await removeClientExternalVendorId(effectiveClientId);
            setAlert(() => ({
                message: response.data.message || 'Plaintiff unlinked successfully.',
                type: 'success',
            }));
            setIsSynced(false);
            setNetSuiteLink(null);
            onSyncStateChange?.(false);
            setIsUnlinkConfirmOpen(false);
            onSync && onSync();
        } catch (error) {
            const axiosError = error as AxiosError<ErrorData>;
            setAlert(() => ({
                message: axiosError.response?.data?.message || 'Failed to unlink plaintiff.',
                type: 'error',
            }));
        }
    };

    return (
        <>
            <Accordion defaultExpanded className={styles.classes.accordionRoot}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                    className={styles.classes.accordionSummary}
                >
                    <Box display='flex' alignItems='center' width='100%' justifyContent='space-between'>
                        <Typography>Net Settlement</Typography>
                        <Typography className={styles.classes.labelText}>{numberToUSDCurrency(settlementSummary?.netSettlementTotal ?? 0)}</Typography>
                    </Box>
                </AccordionSummary>

                <AccordionDetails className={styles.classes.accordionDetails}>
                    <TableContainer
                        className={styles.classes.tableContainer}
                        component={Paper}
                        elevation={0}
                    >
                        <Table sx={{ minWidth: 430 }} size='small'>
                            <TableHead className={styles.classes.tableHeaderCell}>
                                <TableRow>
                                    <TableCell key={'name'} sx={hasAccounting ? { width: '27%' } : {}}>{'Client Name'}</TableCell>
                                    <TableCell key={'amount'} sx={hasAccounting ? { width: '14%' } : {}}>{'Amount'}</TableCell>
                                    <TableCell key={'disposition'} sx={hasAccounting ? { width: '17%' } : {}}>{'Intent'}</TableCell>
                                    <TableCell key={'paymentMethod'} sx={hasAccounting ? { width: '17%' } : {}}>{'Payment Method'}</TableCell>
                                    <TableCell key={'Action'} sx={hasAccounting ? { width: '25%' } : {}}>{'Action'}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody className={clsx(styles.classes.tableBodyCell)}>
                                {netSettlementFields && typeof netSettlementFields === 'object' && Object.keys(netSettlementFields).length > 0 ? (
                                    <TableRow key={'netSettlement-row'}>
                                        <TableCell sx={{ maxWidth: '220px' }}>
                                            <Box display="flex"gap={'2px'} flexDirection="column" overflow="hidden">
                                                <Box display="flex" alignItems="center" flexDirection="row" gap="8px" overflow="hidden">
                                                    <TruncatedText text={netSettlementFields.name || '-'} maxLength={30} />
                                                    {netSuiteLink && (
                                                        <Tooltip
                                                            title={`View Client in ${NETSUITE_LABEL}`}
                                                            arrow
                                                            componentsProps={{
                                                                tooltip: {
                                                                    sx: (theme) => ({
                                                                        backgroundColor: theme.palette.common.white,
                                                                        color: theme.palette.primary.light,
                                                                        borderRadius: '5px',
                                                                        boxShadow: '3px',
                                                                        fontFamily: 'Inter',
                                                                        fontSize: '13px',
                                                                        fontWeight: 500,
                                                                        padding: '5px',
                                                                        maxWidth: '140px',
                                                                    }),
                                                                },
                                                            }}
                                                        >
                                                            <LaunchIcon
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    color: 'common.white',
                                                                    fontSize: '20px',
                                                                    flexShrink: 0,
                                                                }}
                                                                onClick={() => window.open(netSuiteLink, '_blank')}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                    {netSettlementFields?.accounting && (
                                                        <Box
                                                            sx={{
                                                                backgroundColor: isSynced ? 'success.main' : 'warning.main',
                                                                color: 'white',
                                                                fontSize: '10px',
                                                                padding: '2px 6px',
                                                                borderRadius: '8px',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            {isSynced ? 'Synced' : 'Unsynced'}
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" gap={'2px'} flexDirection="column">
                                                <Typography sx={{ color: '#FFFFFF', fontSize: '14px', fontFamily: 'Poppins' }}>
                                                    {numberToUSDCurrency(settlementSummary?.netSettlementTotal) || '-'}
                                                </Typography>
                                                {(settlementType === 'STANDARD_PIP' || Boolean(pipAmount)) && (
                                                    <TruncatedText
                                                        text={pipAmount ? `PIP ${numberToUSDCurrency(pipAmount)} included` : 'PIP included'}
                                                        sx={{ '& .MuiTypography-body2': { fontSize: '12px', fontFamily: 'Poppins' } }}
                                                    />
                                                )}
                                                {(settlementType === 'LIEN_REPAYMENT' || Boolean(lienReimbursementAmount)) && (
                                                    <TruncatedText
                                                        text={
                                                            lienReimbursementAmount
                                                                ? `Lien reimbursement ${numberToUSDCurrency(lienReimbursementAmount)} included`
                                                                : 'Lien reimbursement included'
                                                        }
                                                        sx={{ '& .MuiTypography-body2': { fontSize: '12px', fontFamily: 'Poppins' } }}
                                                    />
                                                )}
                                            </Box>
                                        </TableCell>

                                        <TableCell>
                                            {isNetSettlementZero ? '-' : (netSettlementFields.disposition || '-')}
                                        </TableCell>
                                        <TableCell>
                                            {isNetSettlementZero ? '-' : (netSettlementFields.paymentMethod || '-')}
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap="8px" flexWrap="nowrap">
                                                <Tooltip
                                                    title={
                                                        isReadOnly && isOnboardingReadOnly
                                                            ? 'This is disabled. See the details page for more information.'
                                                            : 'Edit'
                                                    }
                                                    arrow
                                                    componentsProps={{
                                                        tooltip: {
                                                            sx: (theme) => ({
                                                                backgroundColor: theme.palette.common.white,
                                                                color: theme.palette.primary.light,
                                                                borderRadius: '5px',
                                                                boxShadow: '3px',
                                                                fontFamily: 'Inter',
                                                                fontSize: '13px',
                                                                fontWeight: 500,
                                                                padding: '5px',
                                                                maxWidth: '140px',
                                                            }),
                                                        },
                                                    }}>
                                                    <EditIcon
                                                        sx={{
                                                            cursor: isReadOnly ? 'not-allowed' : 'pointer',
                                                            color: isReadOnly ? 'text.disabled' : 'common.white',
                                                            opacity: isReadOnly ? 0.5 : 1,
                                                        }}
                                                        onClick={() => !isReadOnly && handleMenuOptionClick('Edit', 0)}
                                                        fontSize='small'
                                                    />
                                                </Tooltip>
                                                {netSettlementFields?.accounting && !isSynced && isWaveOne && (
                                                    <Button
                                                        variant="contained"
                                                        startIcon={<SearchIcon sx={{ fontSize: '16px' }} />}
                                                        onClick={handleSyncButtonClick}
                                                        sx={{
                                                            backgroundColor: theme.palette.info.main,
                                                            color: 'white',
                                                            textTransform: 'none',
                                                            fontSize: '14px',
                                                            fontWeight: 500,
                                                            padding: '8px 8px',
                                                            width: '184px',
                                                            borderRadius: '12px',
                                                            whiteSpace: 'nowrap',
                                                            '&:hover': { backgroundColor: theme.palette.info.dark },
                                                        }}
                                                    >
                                                        Match plaintiff
                                                    </Button>
                                                )}
                                                {netSettlementFields?.accounting && isSynced && isWaveOne && (
                                                    <>
                                                        <Button
                                                            variant="contained"
                                                            startIcon={<LinkIcon sx={{ fontSize: '16px' }} />}
                                                            onClick={handleRelinkButtonClick}
                                                            sx={{
                                                                backgroundColor: theme.palette.info.main,
                                                                color: 'white',
                                                                textTransform: 'none',
                                                                fontSize: '14px',
                                                                fontWeight: 500,
                                                                padding: '8px 8px',
                                                                width: '88px',
                                                                borderRadius: '12px',
                                                                whiteSpace: 'nowrap',
                                                                '&:hover': { backgroundColor: theme.palette.info.dark },
                                                            }}
                                                        >
                                                            Relink
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            startIcon={<LinkOffIcon sx={{ fontSize: '16px' }} />}
                                                            onClick={() => setIsUnlinkConfirmOpen(true)}
                                                            sx={{
                                                                borderColor: theme.palette.error.main,
                                                                color: theme.palette.error.main,
                                                                textTransform: 'none',
                                                                fontSize: '14px',
                                                                fontWeight: 500,
                                                                padding: '8px 8px',
                                                                width: '88px',
                                                                borderRadius: '12px',
                                                                whiteSpace: 'nowrap',
                                                                '&:hover': {
                                                                    borderColor: theme.palette.error.dark,
                                                                    backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                                                },
                                                            }}
                                                        >
                                                            Unlink
                                                        </Button>
                                                    </>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={5} align='center' sx={{ textAlign: 'center !important' }}>
                                            <Typography variant='body2' color='textSecondary'>
                                                No Net Settlement record found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>


                    <Box className={styles.classes.netSettlementSummary}>
                        <Box className={styles.classes.summaryRow} sx={{ paddingBottom: '8px', }}>
                            <Typography className={styles.classes.summaryKeyNet}>Net Settlement Calculations</Typography>
                        </Box>
                        <Box className={styles.classes.summaryRow}>
                            <Typography className={styles.classes.summaryKeyGross}>Settled Amount</Typography>
                            <Typography className={styles.classes.summaryValue}>
                                {settlementType === 'STANDARD_PIP' && pipAmount
                                    ? `${numberToUSDCurrency((Number(settledAmount) || 0) + pipAmount)}  (${numberToUSDCurrency(settledAmount ?? '')}  +  PIP ${numberToUSDCurrency(pipAmount)})`
                                    : settlementType === 'LIEN_REPAYMENT' && lienReimbursementAmount
                                        ? `${numberToUSDCurrency((Number(settledAmount) || 0) + lienReimbursementAmount)}  (${numberToUSDCurrency(settledAmount ?? '')}  +  Lien reimbursement ${numberToUSDCurrency(lienReimbursementAmount)})`
                                        : numberToUSDCurrency(settledAmount ?? '')}
                            </Typography>
                        </Box>

                        <Box className={styles.classes.summaryRow}>
                            <Typography className={styles.classes.summaryKeyDeduction}>Attorney’s Fees</Typography>
                            <Typography className={styles.classes.summaryValue}>{numberToUSDCurrency(settlementSummary?.attorneysFeesTotal ?? '')}</Typography>
                        </Box>
                        <Box className={styles.classes.summaryRow}>
                            <Typography className={styles.classes.summaryKeyDeduction}>Deductions</Typography>
                            <Typography className={styles.classes.summaryValue}>{numberToUSDCurrency(settlementSummary?.total ?? '')}</Typography>
                        </Box>

                        {/* INDENTED CHILD ROWS */}
                        <Box className={styles.classes.summaryRow}>
                            <Typography className={styles.classes.summaryKeyIndented}>
                                Liens
                            </Typography>
                            <Typography className={styles.classes.summaryValueIndented}>{numberToUSDCurrency(settlementSummary?.liens ?? '')}</Typography>
                        </Box>

                        <Box className={styles.classes.summaryRow}>
                            <Typography className={styles.classes.summaryKeyIndented}>
                                Liens - Withheld
                            </Typography>
                            <Typography className={styles.classes.summaryValueIndented}>{numberToUSDCurrency(settlementSummary?.liensWithheld ?? '')}</Typography>
                        </Box>

                        <Box className={styles.classes.summaryRow}>
                            <Typography className={styles.classes.summaryKeyIndented}>
                                Service Provider
                            </Typography>
                            <Typography className={styles.classes.summaryValueIndented}>{numberToUSDCurrency(settlementSummary?.serviceProviders ?? '')}</Typography>
                        </Box>

                        <Box className={styles.classes.summaryRow}>
                            <Typography className={styles.classes.summaryKeyIndented}>
                                Service Provider - Withheld
                            </Typography>
                            <Typography className={styles.classes.summaryValueIndented}>{numberToUSDCurrency(settlementSummary?.serviceProvidersWithheld ?? '')}</Typography>
                        </Box>

                        <Box className={styles.classes.summaryRow}>
                            <Typography className={styles.classes.summaryKeyIndented}>
                                Expenses
                            </Typography>
                            <Typography className={styles.classes.summaryValueIndented}>{numberToUSDCurrency(settlementSummary?.advances ?? '')}</Typography>
                        </Box>

                        <Box className={styles.classes.summaryRow}>
                            <Typography className={styles.classes.summaryKeyIndented}>
                                Expenses - Withheld
                            </Typography>
                            <Typography className={styles.classes.summaryValueIndented}>{numberToUSDCurrency(settlementSummary?.advancesWithheld ?? '')}</Typography>
                        </Box>


                        <Box className={styles.classes.summaryRow}>
                            <Typography className={styles.classes.summaryKeyBold}>Net Settlement</Typography>
                            <Typography className={styles.classes.summaryValue}>{numberToUSDCurrency(settlementSummary?.netSettlementTotal ?? '')}</Typography>
                        </Box>
                    </Box>

                </AccordionDetails>
            </Accordion>

            {isWaveOne && (
                <>
                    <FindNetSuiteVendorPopup
                        open={isNetSuitePopupOpen}
                        lienName={plaintiffName}
                        searchTerm={plaintiffName}
                        entityType="Plaintiff"
                        entityLabel="Plaintiff"
                        entityId={effectiveClientId}
                        onClose={() => setIsNetSuitePopupOpen(false)}
                        onVendorSelectFromNetsuite={handleNetSuiteVendorSelect}
                        syncAction={syncAction}
                    />

                    <ConfirmationPopup
                        open={isUnlinkConfirmOpen}
                        title="Confirm Unlink"
                        message={`Are you sure you want to unlink this Plaintiff ${plaintiffName} from ${vendorType}? This action will remove the connection between V.Law and ${vendorType} for this plaintiff.`}
                        confirmText="Unlink"
                        onConfirm={handleConfirmUnlink}
                        onCancel={() => setIsUnlinkConfirmOpen(false)}
                    />
                </>
            )}
        </>
    );
};

export default NetSettlementTable;
